/**
 * `platformGetConfiguration` (`GET /config`, `api/paths/platform/config.yaml`, `C7-D06`) —
 * DIFFERENT from `boot/config.ts`'s static `config.json` fetch. That one is deploy-time
 * (`environment`, `apiBaseUrl`, §31.7); this one is a real, unauthenticated API call the client
 * makes once it knows the home host, returning the region catalogue, the refresh single-flight
 * queue's deadline and the one third-party challenge integration's public parameters.
 *
 * **Fetched, not yet consumed.** `main.tsx` calls this during boot and the result is held here so
 * nothing is lost, but the two behaviours it exists to specify — resolving a workspace-scoped
 * call to its own region's host, and bounding the refresh replay queue by
 * `session.refresh_grace_seconds` rather than an invented client constant — are not built yet.
 * Both need the global response interceptor this pass's other gaps already name (no interceptor
 * exists to queue a replay against, and every call in this app still goes to the one home host).
 * Reported here rather than silently left unfetched.
 */
import { getApiClient, ApiProblemError, type Problem } from '@/data/apiClient'
import type { components } from '@/api/generated/schema'

export type BootConfiguration = components['schemas']['BootConfiguration']

let bootConfiguration: BootConfiguration | undefined

export async function loadBootConfiguration(): Promise<BootConfiguration> {
  const client = getApiClient()
  const { data, error } = await client.GET('/config', {})
  if (error) throw new ApiProblemError(error as Problem)
  bootConfiguration = data as BootConfiguration
  return bootConfiguration
}

export function getBootConfiguration(): BootConfiguration | undefined {
  return bootConfiguration
}

/** Test-only: primes the held configuration without a real `/config` round trip — `ob-wz-3` (W153)
 * is the first real reader of `regions`, and a unit test mounting it has no boot sequence to run
 * first. Mirrors `resetOnSignOut()`'s own "a module singleton needs an explicit test seam" shape. */
export function setBootConfigurationForTests(config: BootConfiguration): void {
  bootConfiguration = config
}
